import { types, flow, applySnapshot, cast } from 'mobx-state-tree';

import { ApiStore } from '@src/features/common';
import { toCamelCase, deleteNilKeys, isCompanyDomain } from '@src/helpers';
import { toast } from 'src/components';

const Resume = types
  .model('ResumeModel', {
    resumeUrl: types.string,
    filename: types.string,
    email: types.string,
    user: types.maybeNull(types.frozen()),
    isChecked: types.boolean,
    emailEdited: false
  })
  .actions(self => ({
    update: function(newFields) {
      applySnapshot(self, { ...self, ...newFields });
    }
  }));

export const AddCandidate = types
  .compose(
    ApiStore,
    types.model('AddCandidateModal', {
      isUploadingResume: types.maybe(types.boolean),
      jobs: types.array(types.frozen()),
      jobSteps: types.array(types.frozen()),
      resumes: types.array(types.maybe(Resume))
    })
  )
  .actions(self => {
    return {
      clear() {
        self.resumes = cast([]);
      },
      fetchData: flow(function* fetch() {
        const url = isCompanyDomain() ? '/companies/companyId/jobs?type=active' : '/recruiters/jobs';
        const res = yield self.fetch(url);
        self.jobs = cast(res);
      }),
      getJobSteps: flow(function* fetch(jobId) {
        const url = isCompanyDomain() ? `/companies/companyId/jobs/${jobId}/steps` : `/recruiters/jobs/${jobId}/steps`;
        const res = yield self.fetch(url);
        self.jobSteps = res;
      }),

      updateResume: function(filename, updatedObj) {
        const resume = self.resumes.find(res => res.filename === filename);
        resume?.update(deleteNilKeys(updatedObj));

        if (resume && !resume.emailEdited && resume.user) {
          resume.emailEdited = true;
        }
      },
      postCandidates: flow(function* fetch(data, cb) {
        const url = isCompanyDomain() ? '/companies/companyId/applicant_uploads' : '/recruiters/applicant_uploads';
        yield self.post(url, data);
        cb();
        if (self.state === 'done') {
          toast.info(
            `${data.cvs_attributes.length} candidate${
              data.cvs_attributes.length > 1 ? 's' : ''
            } was added. We are now looking for their most up to date information. This will be updated soon.`
          );
        }
      }),
      sendResume: flow(function* fetch(file: FormData) {
        self.isUploadingResume = true;
        try {
          const url = isCompanyDomain() ? '/companies/companyId/applicant_uploads/resume' : '/recruiters/resume';
          const resumeRaw = yield self.post(url, file);
          const resume = toCamelCase(resumeRaw);
          const isResumeExist = Boolean(self.resumes.find(res => res.filename === resume.filename));
          if (!isResumeExist) {
            self.resumes.push({ ...resume, isChecked: true });
          }
        } catch (error) {
          // all error handling in self.post
        } finally {
          self.isUploadingResume = false;
        }
      })
    };
  });
